import React, { memo, useEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import DefaultView from "../components/DefaultView";
import EmailLeftbar from "../components/EmailLeftbar";
import EmailListItem from "../components/EmailListItem";
import EmptyResult from "../components/EmptyResult";
import Swal from "sweetalert2";
import {
  actionGetAllTrashedMessages,
  actionRemoveMessageFromTrash,
} from "../store/actions/inboxAction";

const Trash = memo(
  ({
    actionGetAllTrashedMessages,
    getTrashedMessagesLoader,
    trashedMessages,
    trashedMessagesCount,
    removeFromTrashLoader,
    actionRemoveMessageFromTrash,
  }) => {
    const [searchText, setSearchText] = useState("");
    const [filteredEmailListItems, setFilteredEmailListItems] = useState([]);
    const [showEmailDetailID, setShowEmailDetailID] = useState("");

    const offset = useRef(0);
    const [itemsPerPage] = useState(20);

    const { pathname } = useLocation();
    const navigate = useNavigate();

    const tooltipInstances = useRef([]);

    useEffect(() => {
      actionGetAllTrashedMessages(offset.current);
    }, [pathname, actionGetAllTrashedMessages]);

    const handleSearch = () => {
      actionGetAllTrashedMessages(0, searchText);
      offset.current = 0;
    };

    useEffect(() => {
      const filtered =
        trashedMessages &&
        trashedMessages.filter((record) => {
          return record.msg.msg_senderName
            .toLowerCase()
            .includes(searchText.toLowerCase());
        });

      setFilteredEmailListItems(filtered);
    }, [searchText]);

    useMemo(() => {
      setFilteredEmailListItems(
        trashedMessages.map((msg) => ({ ...msg, isSelected: false }))
      );
      if (trashedMessages.length < 1) {
        // setOffset(offset);
      }
    }, [trashedMessages]);

    const handleRefresh = () => {
      offset.current = 0;
      setSearchText("");
      actionGetAllTrashedMessages(offset.current);
    };

    const handleNextClick = () => {
      offset.current += 1;
      actionGetAllTrashedMessages(offset.current, searchText ? searchText : "");
    };

    const handleBackClick = () => {
      const newOffset = offset.current - 1;

      actionGetAllTrashedMessages(newOffset, searchText ? searchText : "");
      offset.current = newOffset;
    };

    const handleSelectThisPage = () => {
      // Select all messages on current page locally
      const pageData = filteredEmailListItems.map((m) => ({
        ...m,
        isSelected: true,
      }));
      setFilteredEmailListItems(pageData);
    };

    const handleSelectAll = () => {
      // Select all messages across all pages locally
      const allSelected = filteredEmailListItems.map((m) => ({
        ...m,
        isSelected: true,
      }));
      setFilteredEmailListItems(allSelected);
    };

    const handleDeSelectAll = () => {
      setFilteredEmailListItems(
        filteredEmailListItems.map((m) => ({
          ...m,
          isSelected: false,
        }))
      );
    };

    const handleSelectMessage = (index) => {
      const newFilteredEmailListItems = filteredEmailListItems.map((m, i) => {
        if (index === i) {
          return { ...m, isSelected: !m.isSelected };
        } else {
          return m;
        }
      });
      setFilteredEmailListItems(newFilteredEmailListItems);
    };

    const handleStarred = (index, type) => {
      // Trash items don't support starring, so this is a no-op
      // But the function needs to exist for EmailListItem component
    };

    const handleRemoveFromTrash = () => {
      // Clean up tooltips before delete operation
      tooltipInstances.current.forEach(tooltip => {
        try {
          tooltip.dispose();
        } catch (error) {
          // Ignore disposal errors
        }
      });
      tooltipInstances.current = [];

      const selectedIds = filteredEmailListItems
        .filter((m) => m.isSelected)
        .map((m) => m.msg.trash_msg_ID);

      // Check if user wants to select all across pages
      const hasSelectedItems = selectedIds.length > 0;
      const allCurrentPageSelected = filteredEmailListItems.length > 0 &&
        filteredEmailListItems.every(item => item.isSelected);

      // Determine if this should be treated as select_all operation
      const isSelectAllOperation = allCurrentPageSelected && trashedMessagesCount > filteredEmailListItems.length;

      const getMessageParams = {
        offset: offset.current,
        searchText,
      };

      if (!hasSelectedItems && !isSelectAllOperation) {
        Swal.fire({
          title: "No Selection",
          text: "Please select at least one message to delete.",
          icon: "warning",
          confirmButtonText: "OK"
        });
        return;
      }

      // Confirmation before delete
      const messageText = isSelectAllOperation
        ? `Do you want to permanently delete all ${trashedMessagesCount} message(s) in trash?`
        : `Do you want to permanently delete the selected ${selectedIds.length} message(s)?`;

      Swal.fire({
        title: "Are you sure?",
        text: messageText,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, delete",
      }).then((result) => {
        if (result.isConfirmed) {
          const deleteParams = {
            trash_msg_ID: isSelectAllOperation ? [0] : selectedIds,
            select_all: isSelectAllOperation ? "YES" : "NO"
          };

          actionRemoveMessageFromTrash(deleteParams, getMessageParams);
        }
      });
    };

    const showingFrom = offset.current * itemsPerPage + 1;
    const showingTo = Math.min(
      (offset.current + 1) * itemsPerPage,
      trashedMessagesCount
    );

    const BackAndNextButtonsGroup = () => (
      <div className="btn-group float-end">
        <button
          type="button"
          className="btn btn-sm btn-success waves-effect"
          onClick={handleBackClick}
          disabled={offset.current === 0}
        >
          <i className="fa fa-chevron-left" />
        </button>
        <button
          type="button"
          className="btn btn-sm btn-success waves-effect"
          onClick={handleNextClick}
          disabled={
            (offset.current + 1) * itemsPerPage >= trashedMessagesCount ||
            !trashedMessagesCount ||
            !filteredEmailListItems.length
          }
        >
          <i className="fa fa-chevron-right" />
        </button>
      </div>
    );

    // Initialize Bootstrap tooltips
    useEffect(() => {
      // Clean up existing tooltips first
      tooltipInstances.current.forEach(tooltip => {
        try {
          tooltip.dispose();
        } catch (error) {
          // Ignore disposal errors for already disposed tooltips
        }
      });
      tooltipInstances.current = [];

      if (window.bootstrap && window.bootstrap.Tooltip) {
        const tooltipTriggerList = [].slice.call(
          document.querySelectorAll('[data-bs-toggle="tooltip"]')
        );
        tooltipInstances.current = tooltipTriggerList.map(function (tooltipTriggerEl) {
          return new window.bootstrap.Tooltip(tooltipTriggerEl);
        });
      }

      // Cleanup tooltips on unmount
      return () => {
        tooltipInstances.current.forEach(tooltip => {
          try {
            tooltip.dispose();
          } catch (error) {
            // Ignore disposal errors
          }
        });
        tooltipInstances.current = [];
      };
    }, [filteredEmailListItems]);

    return (
      <div className="page-content">
        <div className="container-fluid">
          {/* start page title */}
          <div className="row">
            <div className="col-12">
              <div className="page-title-box d-sm-flex align-items-center justify-content-between">
                <h4 className="mb-sm-0 main_heading">Trash</h4>
                <div className="page-title-right">
                  <DefaultView />
                </div>
              </div>
            </div>
          </div>
          {/* end page title */}
          <div className="row">
            <div className="col-12">
              {/* Left sidebar */}
              <EmailLeftbar />
              {/* End Left sidebar */}

              {/* Right Sidebar */}
              <div className="email-rightbar mb-3">
                <div className="card">
                  <div
                    className="btn-toolbar gap-2 p-3 d-flex justify-content-between"
                    role="toolbar"
                  >
                    <div>
                      <div className="btn-group" style={{ marginRight: 3 }}>
                        <button
                          type="button"
                          className="btn btn-primary waves-light waves-effect dropdown-toggle d-flex"
                          data-bs-toggle="dropdown"
                          aria-expanded="false"
                        >
                          <div className="checkbox-wrapper-mail">
                            <input
                              type="checkbox"
                              id={`chk_select`}
                              readOnly
                              checked={false}
                            />
                            <label
                              htmlFor={`chk_select`}
                              className="toggle"
                              onClick={(e) => e.stopPropagation()}
                            />
                          </div>{" "}
                          <i className="mdi mdi-chevron-down ms-1" />
                        </button>
                        <div className="dropdown-menu">
                          <div
                            className="dropdown-item"
                            style={{ cursor: "pointer" }}
                            onClick={handleSelectThisPage}
                          >
                            Select This Page
                          </div>
                          <div
                            className="dropdown-item"
                            style={{ cursor: "pointer" }}
                            onClick={handleSelectAll}
                          >
                            Select All
                          </div>
                          <div
                            className="dropdown-item"
                            style={{ cursor: "pointer" }}
                            onClick={handleDeSelectAll}
                          >
                            None
                          </div>
                        </div>
                      </div>
                      <div className="btn-group">
                        {filteredEmailListItems.filter((m) => m.isSelected)
                          .length > 0 && (
                            <>
                              {
                                <button
                                  onClick={handleRemoveFromTrash}
                                  data-bs-toggle="tooltip"
                                  data-bs-original-title="Delete from Trash"
                                  title="Delete from Trash"
                                  type="button"
                                  className="btn btn-primary waves-light waves-effect"
                                >
                                  {removeFromTrashLoader ? (
                                    <i
                                      className="fas fa-spinner fa-spin"
                                      data-bs-toggle="tooltip"
                                      data-bs-original-title="Loading"
                                    ></i>
                                  ) : (
                                    <i className="far fa-trash-alt" />
                                  )}
                                </button>
                              }
                            </>
                          )}
                      </div>
                      <div
                        className="btn btn-primary"
                        data-bs-toggle="tooltip"
                        data-bs-original-title="Refresh"
                        style={{ cursor: "pointer", marginLeft: 10 }}
                        onClick={() => handleRefresh()}
                      >
                        <i className="fa fa-refresh" aria-hidden="true"></i>
                      </div>
                    </div>
                    <div className="d-flex">
                      <form
                        className="app-search d-none d-lg-block"
                        style={{ float: "left" }}
                        onSubmit={(e) => {
                          e.preventDefault();
                          handleSearch();
                        }}
                      >
                        <div className="position-relative">
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Search..."
                            value={searchText}
                            onChange={(e) => {
                              setSearchText(e.target.value);
                              if (e.target.value.trim() === "") {
                                actionGetAllTrashedMessages(offset.current);
                              }
                            }}
                          />
                          <button
                            className="btn btn-primary"
                            type="button"
                            onClick={handleSearch}
                          >
                            <i className="bx bx-search-alt align-middle" />
                          </button>
                        </div>
                      </form>
                      <div className="btn-group ms-2">
                        <button
                          type="button"
                          className="btn btn-sm btn-success waves-effect"
                          onClick={handleBackClick}
                          disabled={offset.current === 0}
                        >
                          <i className="fa fa-chevron-left" />
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-success waves-effect"
                          onClick={handleNextClick}
                          disabled={
                            (offset.current + 1) * itemsPerPage >=
                            trashedMessagesCount ||
                            !trashedMessagesCount ||
                            !filteredEmailListItems.length
                          }
                        >
                          <i className="fa fa-chevron-right" />
                        </button>
                      </div>
                    </div>
                  </div>
                  <ul className="message-list faxMessage">
                    {/* Table-style skeleton while loading */}
                    {getTrashedMessagesLoader && (
                      <>
                        {[...Array(8)].map((_, i) => (
                          <li
                            key={i}
                            className="message-list-item d-flex align-items-center"
                            style={{
                              cursor: "pointer",
                              padding: "12px 16px",
                              borderBottom: "1px solid rgba(0,0,0,0.05)",
                              listStyle: "none",
                              gap: 12,
                            }}
                          >
                            <div
                              style={{
                                width: 60,
                                display: "flex",
                                alignItems: "center",
                                gap: 10,
                              }}
                            >
                              <div
                                className="skeleton"
                                style={{
                                  width: 20,
                                  height: 20,
                                  borderRadius: 3,
                                }}
                              />
                              <div
                                className="skeleton"
                                style={{
                                  width: 16,
                                  height: 16,
                                  borderRadius: 2,
                                }}
                              />
                            </div>
                            <div style={{ flex: 1, minWidth: 200 }}>
                              <div
                                className="skeleton"
                                style={{ width: "40%", height: 14 }}
                              />
                              <div
                                className="skeleton"
                                style={{
                                  width: "30%",
                                  height: 12,
                                  marginTop: 8,
                                }}
                              />
                            </div>
                            <div style={{ width: 120, minWidth: 120 }}>
                              <div
                                className="skeleton"
                                style={{ width: 60, height: 12 }}
                              />
                            </div>
                            <div
                              style={{
                                width: 160,
                                minWidth: 140,
                                textAlign: "right",
                              }}
                            >
                              <div
                                className="skeleton"
                                style={{
                                  width: 110,
                                  height: 12,
                                  marginLeft: "auto",
                                }}
                              />
                            </div>
                          </li>
                        ))}
                      </>
                    )}

                    {/* Empty state using EmptyResult */}
                    {!getTrashedMessagesLoader &&
                      (!trashedMessages || trashedMessages.length < 1) && (
                        <EmptyResult
                          title={"No Messages Found"}
                          description={"You currently have no messages."}
                        />
                      )}

                    {/* Actual list */}
                    {!getTrashedMessagesLoader &&
                      filteredEmailListItems.map((item, index) => (
                        <EmailListItem
                          key={index}
                          index={index}
                          senderName={item.msg.msg_senderName}
                          senderPhone={item.msg.msg_fromNumber}
                          noOfPages={
                            item.msg.msg_totalPages
                              ? item.msg.msg_totalPages
                              : 0
                          }
                          date={item.msg.msg_time}
                          isUnread={parseInt(item.msg.is_read)}
                          isChecked={item.isSelected}
                          handleSelectMessage={handleSelectMessage}
                          handleStarred={handleStarred}
                          detailId={item.msg.trash_msg_ID}
                          onNavigateToDetail={(detailId) => {
                            navigate(`/read-mail/trash/${detailId}`, {
                              state: {
                                type: "TRASH",
                                getMessageParams: {
                                  offset: offset.current,
                                  searchText,
                                },
                              },
                            });
                          }}
                        />
                      ))}
                  </ul>
                </div>{" "}
                {/* card */}
                <div className="row">
                  <div className="col-7">
                    Showing {trashedMessagesCount > 0 ? showingFrom : 0} -{" "}
                    {trashedMessagesCount > 0 ? showingTo : 0} of{" "}
                    {trashedMessagesCount ? trashedMessagesCount : 0}
                  </div>
                  <div className="col-5">
                    <BackAndNextButtonsGroup />
                  </div>
                </div>
              </div>
              {/* end  Right Sidebar Col-9 */}
            </div>
          </div>
        </div>
      </div>
    );
  }
);

const mapStateToProps = (state) => {
  return {
    getTrashedMessagesLoader: state.inbox.getTrashedMessagesLoader,
    trashedMessages: state.inbox.trashedMessages,
    trashedMessagesCount: state.inbox.trashedMessagesCount,
    removeFromTrashLoader: state.inbox.removeFromTrashLoader,
  };
};

export default connect(mapStateToProps, {
  actionGetAllTrashedMessages,
  actionRemoveMessageFromTrash,
})(Trash);
